import { useEffect, useState } from "react";

// MUI
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

// Custom
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import useApi from "components/Utils/api";
import toast from "react-hot-toast";

function BankTLSConfig() {
  const api = useApi();

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [formData, setFormData] = useState({
    bankName: "",
    hostCertificate: "",
    clientCertificate: "",
    clientKey: "",
  });

  // 🔥 Preview State
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState("");
  const [previewType, setPreviewType] = useState("");

  // ================== BASE64 ==================
  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  // ================== FILE TYPE ==================
  const getFileType = (base64) => {
    if (!base64) return "unknown";

    // already has prefix
    if (base64.startsWith("data:")) {
      if (base64.includes("image")) return "image";
      if (base64.includes("pdf")) return "pdf";
      if (base64.includes("text")) return "text";
    }

    // raw base64 detection
    if (base64.startsWith("iVBOR") || base64.startsWith("/9j/")) {
      return "image";
    }

    if (base64.startsWith("JVBER")) {
      return "pdf";
    }

    if (base64.startsWith("UEsDB")) {
      return "binary"; // zip/docx
    }

    return "text"; // default for certs
  };

  // ================== PREVIEW ==================
  const handlePreview = (base64) => {
    const type = getFileType(base64);
    setPreviewType(type);
    setPreviewData(base64);
    setPreviewOpen(true);
  };

  const renderPreviewCell = (base64) => {
    if (!base64) return "N/A";

    const type = getFileType(base64);

    return (
      <MDBox
        onClick={() => handlePreview(base64)}
        sx={{
          cursor: "pointer",
          px: 1.5,
          py: 0.5,
          borderRadius: "8px",
          backgroundColor: "#f5f5f5",
          fontSize: "12px",
          fontWeight: "500",
          textAlign: "center",
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
          "&:hover": { backgroundColor: "#e0e0e0" },
        }}
      >
        <Icon fontSize="small">{type === "image" ? "image" : "description"}</Icon>
        View
      </MDBox>
    );
  };

  // ================== TABLE ==================
  const columns = [
    { Header: "Bank Name", accessor: "bankName", align: "left" },
    { Header: "Host Certificate", accessor: "hostCertificate", align: "center" },
    { Header: "Client Certificate", accessor: "clientCertificate", align: "center" },
    { Header: "Client Key", accessor: "clientKey", align: "center" },
    { Header: "Action", accessor: "action", align: "center" },
  ];

  const fetchList = () => {
    setLoading(true);
    api
      .get("/tls/all")
      .then((res) => {
        const rows = res.data.map((item) => ({
          ...item,

          hostCertificate: renderPreviewCell(item.hostCertificate),
          clientCertificate: renderPreviewCell(item.clientCertificate),
          clientKey: renderPreviewCell(item.clientKey),

          action: (
            <MDBox display="flex" justifyContent="center" gap={1}>
              <IconButton size="small" color="info" onClick={() => handleEdit(item)}>
                <Icon>edit</Icon>
              </IconButton>

              <IconButton size="medium" color="error" onClick={() => handleDelete(item.bankName)}>
                <Icon>delete</Icon>
              </IconButton>
            </MDBox>
          ),
        }));

        setList(rows);
      })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };

  // ================== HANDLERS ==================
  const handleCreate = () => {
    setIsEdit(false);
    setFormData({
      bankName: "",
      hostCertificate: "",
      clientCertificate: "",
      clientKey: "",
    });
    setOpenDialog(true);
  };

  const handleEdit = (item) => {
    setIsEdit(true);
    setFormData({
      bankName: item.bankName,
      hostCertificate: "",
      clientCertificate: "",
      clientKey: "",
    });
    setOpenDialog(true);
  };

  const handleDelete = (bankName) => {
    if (!window.confirm(`Delete ${bankName}?`)) return;

    api
      .delete(`/tls/delete/${bankName}`)
      .then(() => {
        toast.success("Deleted");
        fetchList();
      })
      .catch(() => toast.error("Delete failed"));
  };

  // ================== FILE HANDLER ==================
  const handleFileChange = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    const base64 = await toBase64(file);

    setFormData((prev) => ({
      ...prev,
      [field]: base64,
    }));
  };

  // ================== SUBMIT ==================
  const handleSubmit = () => {
    if (!formData.bankName) {
      toast.error("Bank Name required");
      return;
    }

    setLoading(true);

    const apiCall = isEdit
      ? api.put(`/tls/update/${formData.bankName}`, formData)
      : api.post("/tls/create", formData);

    apiCall
      .then(() => {
        toast.success(`TLS ${isEdit ? "updated" : "created"}`);
        setOpenDialog(false);
        fetchList();
      })
      .catch(() => toast.error("Save failed"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchList();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox mt={6}>
        <Grid container>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} display="flex" justifyContent="space-between">
                <MDTypography variant="h6">Bank TLS Config</MDTypography>

                <MDButton color="info" variant="contained" onClick={handleCreate}>
                  Add
                </MDButton>
              </MDBox>

              <MDBox px={3} pb={3}>
                <DataTable table={{ columns, rows: list }} canSearch />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* ================== CREATE / EDIT ================== */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth>
        <DialogTitle>{isEdit ? "Update TLS" : "Create TLS"}</DialogTitle>

        <DialogContent>
          <MDBox mt={2}>
            <Grid container spacing={2}>
              {/* Bank Name */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Bank Name"
                  value={formData.bankName}
                  disabled={isEdit}
                  onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                />
              </Grid>

              {/* Host Certificate */}
              <Grid item xs={12} md={4}>
                <MDTypography variant="caption" fontWeight="medium">
                  Host Certificate
                </MDTypography>
                <TextField
                  fullWidth
                  type="file"
                  inputProps={{ accept: ".crt,.pem,.key,.txt,.pdf,.png,.jpg,.jpeg" }}
                  onChange={(e) => handleFileChange(e, "hostCertificate")}
                />
              </Grid>

              {/* Client Certificate */}
              <Grid item xs={12} md={4}>
                <MDTypography variant="caption" fontWeight="medium">
                  Client Certificate
                </MDTypography>
                <TextField
                  fullWidth
                  type="file"
                  inputProps={{ accept: ".crt,.pem,.key,.txt,.pdf,.png,.jpg,.jpeg" }}
                  onChange={(e) => handleFileChange(e, "clientCertificate")}
                />
              </Grid>

              {/* Client Key */}
              <Grid item xs={12} md={4}>
                <MDTypography variant="caption" fontWeight="medium">
                  Client Key
                </MDTypography>
                <TextField
                  fullWidth
                  type="file"
                  inputProps={{ accept: ".crt,.pem,.key,.txt,.pdf,.png,.jpg,.jpeg" }}
                  onChange={(e) => handleFileChange(e, "clientKey")}
                />
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={() => setOpenDialog(false)}>Cancel</MDButton>
          <MDButton onClick={handleSubmit} disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ================== PREVIEW MODAL ================== */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Preview</DialogTitle>

        <DialogContent>
          {/* IMAGE */}
          {previewType === "image" && (
            <img
              src={
                previewData.startsWith("data:")
                  ? previewData
                  : `data:image/png;base64,${previewData}`
              }
              alt="Preview"
              style={{ width: "100%", borderRadius: "8px" }}
            />
          )}

          {/* PDF */}
          {previewType === "pdf" && (
            <iframe
              title="PDF Preview"
              src={
                previewData.startsWith("data:")
                  ? previewData
                  : `data:application/pdf;base64,${previewData}`
              }
              width="100%"
              height="500px"
              style={{ borderRadius: "8px" }}
            />
          )}

          {/* TEXT / CERT */}
          {previewType === "text" && (
            <MDBox
              sx={{
                backgroundColor: "#000",
                color: "#00ff00",
                padding: "16px",
                borderRadius: "8px",
                fontFamily: "monospace",
                fontSize: "12px",
                maxHeight: "500px",
                overflow: "auto",
                whiteSpace: "pre-wrap",
              }}
            >
              {atob(previewData.replace(/^data:.*;base64,/, ""))}
            </MDBox>
          )}

          {/* UNSUPPORTED */}
          {previewType === "binary" && (
            <MDBox
              sx={{
                textAlign: "center",
                padding: "40px",
                color: "#999",
                fontWeight: "500",
              }}
            >
              ⚠️ Preview not supported for this file type
            </MDBox>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton onClick={() => setPreviewOpen(false)}>Close</MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default BankTLSConfig;